import type { User } from 'firebase/auth';
import * as React from 'react';

import { getIntercomUserHash } from '@gitbook/app-analytics/client';
import { getUserDisplayName } from '@gitbook/app-database';
import { webConfig } from '@gitbook/env';
import { head } from '@gitbook/util-react-head';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { useCurrentUser } from './auth/provider';
import type { ContactSupportFn } from './support-types';

/**
 * Return a callback to start contacting the support.
 */
export function useContactSupportIntercom(): ContactSupportFn {
    const user = useCurrentUser();

    async function updateIntercomUser() {
        if (!window.Intercom || !intercomInitialized || !intercomAvailable) {
            return;
        }

        if (!user) {
            // If the user has logged out, we clear its Intercom browser session
            window.Intercom('shutdown');
            return;
        }

        // Otherwise, we update the user's information
        const intercomUserParams = await getIntercomUserParams(user);
        window.Intercom('update', intercomUserParams);
    }

    // Update the Intercom user whenever the user changes
    React.useEffect(() => {
        void updateIntercomUser();
    }, [user?.uid]);

    return useEventCallback(async () => {
        // Waiting for Intercom Messenger to be ready
        const useIntercom = await loadIntercomMessenger(user);

        if (!useIntercom || !window.Intercom) {
            // If Intercom Messenger is not available, we fallback to an email
            window.open('mailto:support@gitbook.com');
            return;
        }

        window.Intercom('show');
    });
}

let intercomInitialized = false;
let intercomAvailable = false;

async function loadIntercomMessenger(user: User | null): Promise<boolean> {
    // If Intercom has already been initialized, we don't need to do it again
    if (intercomInitialized) {
        return intercomAvailable;
    }

    intercomInitialized = true;

    if (
        process.env.GITBOOK_PREDICTABLE_UI ||
        !webConfig.intercom.appId // Intercom is not defined in this environment
    ) {
        intercomAvailable = false;

        return intercomAvailable;
    }

    await new Promise<void>((resolve) =>
        head.writeAsync({
            type: 'script',
            src: `https://widget.intercom.io/widget/${webConfig.intercom.appId}`,
            onLoad: async () => {
                if (window.Intercom) {
                    const intercomUserParams = await getIntercomUserParams(user);
                    window.Intercom('boot', {
                        app_id: webConfig.intercom.appId,
                        hide_default_launcher: true,
                        ...intercomUserParams,
                    });
                }

                intercomAvailable = true;

                resolve();
            },
        })
    );

    return intercomAvailable;
}

async function getIntercomUserParams(user: User | null) {
    if (!user) {
        return {};
    }

    const intercomUserHash = await getIntercomUserHash(user);
    return {
        email: user.email,
        user_id: user.uid,
        user_hash: intercomUserHash,
        created_at: user.metadata.creationTime,
        name: getUserDisplayName(user),
    };
}
